import { useCallback, useMemo, useState } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { FormInput, FormSelect } from '@/components/forms';
import { useCreateDocumentRequest } from './hooks/useDocuments';
import { useEffectiveFilters } from '@/hooks/useEffectiveFilters';
import { useSystemLookups } from '@/features/settings/hooks/useSystemLookups';
import { useContactsList } from '@/features/contacts/hooks/useContacts';
import { JobSearchModal, type JobSearchResult } from '@/components/JobSearchModal';
import { ClientSearchModal, type ClientSearchResult } from '@/components/ClientSearchModal';
import { useToast } from '@/components/ui/Toast';
import { DOCUMENT_SET_PRESETS, type DocumentSetPreset } from './documentSetPresets';

// ─── Validation Schema ───────────────────────────────────────────────────────

const requiredDocumentSchema = z.object({
  documentType: z.string().min(1, 'Document type is required'),
  description: z.string().optional(),
  isMandatory: z.boolean(),
});

const documentRequestSchema = z.object({
  clientId: z.string().min(1, 'Client is required'),
  jobId: z.string().optional(),
  requiredDocuments: z
    .array(requiredDocumentSchema)
    .min(1, 'At least one document type is required'),
  dueDate: z.string().optional(),
  notes: z.string().optional(),
});

type DocumentRequestFormData = z.infer<typeof documentRequestSchema>;

// ─── Props ───────────────────────────────────────────────────────────────────

interface DocumentRequestFormProps {
  onSuccess?: () => void;
  defaultClientId?: string;
  defaultJobId?: string;
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({
  step,
  title,
  hint,
  action,
  children,
}: {
  step: number;
  title: string;
  hint?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border p-4 dark:border-border-dark">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
            {step}
          </span>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            {hint && <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * Redesigned form to request documents from clients.
 * Sectioned layout, per-item required/optional toggle, document-set presets,
 * and a live summary. Validates: Requirements 9.3
 */
export function DocumentRequestForm({
  onSuccess,
  defaultClientId,
  defaultJobId,
}: DocumentRequestFormProps) {
  const createRequest = useCreateDocumentRequest();
  const { isValueAllowed } = useEffectiveFilters();
  const { data: documentCategories = [] } = useSystemLookups('document_categories');
  const { data: clientsData } = useContactsList({ contactTypeNature: 'client', pageSize: 200 });
  const { toast } = useToast();

  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobSearchResult | null>(null);
  const [showPresets, setShowPresets] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  // Display name for the selected client. Seeded from the contacts list when a
  // defaultClientId is provided; otherwise set from the search modal selection.
  const [selectedClientName, setSelectedClientName] = useState<string>(() => {
    if (!defaultClientId) return '';
    const c = clientsData?.contacts?.find((x) => x.id === defaultClientId);
    return c ? `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.email || '' : '';
  });

  // Allowed document-type options + a code->allowed lookup for preset filtering.
  const allowedDocTypes = useMemo(() => {
    return documentCategories
      .filter((lookup) => lookup.isActive)
      .filter((lookup) => isValueAllowed('documents', 'document_type', lookup.code));
  }, [documentCategories, isValueAllowed]);

  const filteredDocumentTypeOptions = useMemo(() => {
    return [
      { value: '', label: 'Select document type...' },
      ...allowedDocTypes.map((lookup) => ({ value: lookup.code, label: lookup.label })),
    ];
  }, [allowedDocTypes]);

  const allowedCodes = useMemo(
    () => new Set(allowedDocTypes.map((l) => l.code.toLowerCase())),
    [allowedDocTypes],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm<DocumentRequestFormData>({
    resolver: zodResolver(documentRequestSchema),
    defaultValues: {
      clientId: defaultClientId ?? '',
      jobId: defaultJobId ?? '',
      requiredDocuments: [{ documentType: '', description: '', isMandatory: true }],
      dueDate: '',
      notes: '',
    },
  });

  const watchedClientId = useWatch({ control, name: 'clientId' });
  const watchedDocs = useWatch({ control, name: 'requiredDocuments' });
  const watchedDueDate = useWatch({ control, name: 'dueDate' });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'requiredDocuments',
  });

  const clientName = watchedClientId ? selectedClientName || 'Selected client' : null;

  const handleClientSelect = useCallback(
    (client: ClientSearchResult) => {
      setValue('clientId', client.id, { shouldValidate: true });
      setSelectedClientName(client.displayName || client.fullName || '');
      // Changing the client invalidates any previously selected job.
      setSelectedJob(null);
      setValue('jobId', '');
      setIsClientModalOpen(false);
    },
    [setValue],
  );

  const validDocCount = (watchedDocs ?? []).filter((d) => d?.documentType).length;

  const handleJobSelect = useCallback(
    (job: JobSearchResult) => {
      setSelectedJob(job);
      setValue('jobId', job.id);
      setIsJobModalOpen(false);
    },
    [setValue],
  );

  const handleClearJob = useCallback(() => {
    setSelectedJob(null);
    setValue('jobId', '');
  }, [setValue]);

  // Apply a preset: append each of its items whose code is allowed for the
  // tenant. If the first row is still empty, drop it so the list is clean.
  const applyPreset = useCallback(
    (preset: DocumentSetPreset) => {
      const toAdd = preset.items
        .filter((item) => allowedCodes.has(item.code.toLowerCase()))
        .map((item) => ({
          documentType: item.code,
          description: item.description ?? '',
          isMandatory: item.isMandatory !== false,
        }));

      const skipped = preset.items.length - toAdd.length;

      if (toAdd.length === 0) {
        toast({
          variant: 'error',
          title: `None of the "${preset.name}" document types are configured for this firm`,
        });
        return;
      }

      // Remove a single leading empty row so presets don't leave a blank at top.
      const current = watchedDocs ?? [];
      const onlyEmptyRow =
        current.length === 1 && !current[0]?.documentType && !current[0]?.description;
      if (onlyEmptyRow) {
        remove(0);
      }
      append(toAdd);
      setShowPresets(false);
      toast({
        variant: 'success',
        title:
          skipped > 0
            ? `Added ${toAdd.length} document${toAdd.length !== 1 ? 's' : ''} (${skipped} not configured)`
            : `Added ${toAdd.length} document${toAdd.length !== 1 ? 's' : ''} from "${preset.name}"`,
      });
    },
    [allowedCodes, append, remove, watchedDocs, toast],
  );

  const onSubmit = useCallback(
    async (data: DocumentRequestFormData) => {
      try {
        await createRequest.mutateAsync({
          clientId: data.clientId,
          jobId: data.jobId || undefined,
          requiredDocuments: data.requiredDocuments.map((doc) => ({
            documentType: doc.documentType,
            description: doc.description || undefined,
            isMandatory: doc.isMandatory,
          })),
          dueDate: data.dueDate || undefined,
          notes: data.notes || undefined,
        });

        toast({ variant: 'success', title: 'Document request sent successfully' });
        reset();
        setSelectedJob(null);
        onSuccess?.();
      } catch (error: unknown) {
        const message =
          (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data
            ?.error?.message || 'Failed to send document request';
        toast({ variant: 'error', title: message });
      }
    },
    [createRequest, reset, onSuccess, toast],
  );

  const formattedDue = watchedDueDate
    ? new Date(watchedDueDate).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* Intro */}
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Ask a client to upload the documents you need. They'll receive a portal task and a
        notification with your list.
      </p>

      {/* Section 1 — Who */}
      <Section step={1} title="Who is this for?">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Client — opens the shared client search modal */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Client
            </label>
            {watchedClientId ? (
              <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {selectedClientName || 'Selected client'}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsClientModalOpen(true)}
                >
                  Change
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsClientModalOpen(true)}
                className="w-full justify-start text-gray-500"
              >
                <SearchIcon />
                <span className="ml-2">Search and select a client...</span>
              </Button>
            )}
            {errors.clientId?.message && (
              <p className="mt-1 text-sm text-danger-600">{errors.clientId.message}</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Job (optional)
            </label>
            {selectedJob ? (
              <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {selectedJob.jobCode ? `${selectedJob.jobCode} — ` : ''}
                    {selectedJob.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{selectedJob.jobType}</p>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={handleClearJob}>
                  <XIcon />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsJobModalOpen(true)}
                disabled={!watchedClientId}
                className="w-full justify-start text-gray-500"
              >
                <SearchIcon />
                <span className="ml-2">
                  {watchedClientId ? 'Search and select a job...' : 'Select a client first'}
                </span>
              </Button>
            )}
          </div>
        </div>
      </Section>

      {/* Section 2 — Documents */}
      <Section
        step={2}
        title="Documents requested"
        hint={validDocCount > 0 ? `${validDocCount} selected` : 'Add the documents you need'}
        action={
          <div className="relative">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowPresets((v) => !v)}
            >
              + Add a document set
            </Button>
            {showPresets && (
              <>
                <button
                  type="button"
                  aria-hidden
                  className="fixed inset-0 z-10 cursor-default"
                  onClick={() => setShowPresets(false)}
                  tabIndex={-1}
                />
                <div className="absolute right-0 z-20 mt-1 w-72 overflow-hidden rounded-lg border border-border bg-white shadow-lg dark:border-border-dark dark:bg-surface-dark-secondary">
                  {DOCUMENT_SET_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => applyPreset(preset)}
                      className="block w-full border-b border-border px-3 py-2 text-left last:border-b-0 hover:bg-surface-secondary dark:border-border-dark dark:hover:bg-surface-dark-tertiary"
                    >
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {preset.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {preset.description}
                      </p>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        }
      >
        {errors.requiredDocuments?.message && (
          <p className="mb-2 text-sm text-danger-600">{errors.requiredDocuments.message}</p>
        )}

        <div className="space-y-2">
          {fields.map((field, index) => {
            const isMandatory = watchedDocs?.[index]?.isMandatory !== false;
            return (
              <div
                key={field.id}
                className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-surface-dark-secondary"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-brand-50 text-xs font-bold text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                    {index + 1}
                  </span>
                  <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                    <FormSelect
                      name={`requiredDocuments.${index}.documentType`}
                      control={control}
                      label="Document type"
                      options={filteredDocumentTypeOptions}
                    />
                    <FormInput
                      name={`requiredDocuments.${index}.description`}
                      control={control}
                      label="Description (optional)"
                      placeholder="Additional details..."
                    />
                  </div>
                  {fields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(index)}
                      aria-label={`Remove document ${index + 1}`}
                      className="mt-6"
                    >
                      <TrashIcon />
                    </Button>
                  )}
                </div>
                {/* Required / optional toggle */}
                <div className="mt-2 flex items-center gap-2 pl-9">
                  <button
                    type="button"
                    onClick={() =>
                      update(index, {
                        ...(watchedDocs?.[index] ?? { documentType: '', description: '' }),
                        isMandatory: !isMandatory,
                      })
                    }
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                      isMandatory
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                    aria-pressed={isMandatory}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${isMandatory ? 'bg-amber-500' : 'bg-gray-400'}`}
                    />
                    {isMandatory ? 'Required' : 'Optional'}
                  </button>
                  <span className="text-xs text-gray-400">Click to toggle</span>
                </div>
              </div>
            );
          })}
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => append({ documentType: '', description: '', isMandatory: true })}
          className="mt-3"
        >
          <PlusIcon />
          <span className="ml-1">Add another document</span>
        </Button>
      </Section>

      {/* Section 3 — Deadline & notes */}
      <Section step={3} title="Deadline & notes">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FormInput name="dueDate" control={control} label="Due date (optional)" type="date" />
          <div className="sm:col-span-2">
            <FormInput
              name="notes"
              control={control}
              label="Note to client (optional)"
              placeholder="Any additional instructions..."
            />
          </div>
        </div>
      </Section>

      {/* Live summary */}
      {(clientName || validDocCount > 0) && (
        <div className="rounded-lg bg-brand-50 px-4 py-3 text-sm text-brand-800 dark:bg-brand-900/20 dark:text-brand-200">
          {clientName ? <span className="font-medium">{clientName}</span> : 'This client'} will be
          asked for <span className="font-medium">{validDocCount}</span> document
          {validDocCount !== 1 ? 's' : ''}
          {formattedDue ? (
            <>
              , due <span className="font-medium">{formattedDue}</span>
            </>
          ) : (
            ' (no due date)'
          )}
          .
        </div>
      )}

      {/* Client Search Modal (shared picker used across the app) */}
      {isClientModalOpen && (
        <ClientSearchModal
          onSelect={handleClientSelect}
          onClose={() => setIsClientModalOpen(false)}
          title="Search & Select Client"
          contactNature="client"
        />
      )}

      {/* Job Search Modal */}
      {isJobModalOpen && (
        <JobSearchModal
          onSelect={handleJobSelect}
          onClose={() => setIsJobModalOpen(false)}
          clientId={watchedClientId}
          title="Select Job for Document Request"
          defaultStatus=""
          excludeStatuses={['complete', 'cancelled']}
        />
      )}

      {/* Actions */}
      <div className="sticky bottom-0 flex justify-end gap-3 border-t border-gray-200 bg-white pt-4 dark:border-gray-700 dark:bg-surface-dark-secondary">
        <Button type="button" variant="outline" onClick={onSuccess}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting || createRequest.isPending}>
          {createRequest.isPending ? 'Sending...' : 'Send request'}
        </Button>
      </div>
    </form>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function XIcon() {
  return (
    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      className="h-4 w-4 text-gray-400 hover:text-danger-500"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}
